import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  createComponent,
  inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
  BOB_TOAST_DEFAULTS,
  resolveBobToastIcon,
  resolveBobToastPosition,
} from "./bob-toast.config";
import { BobToastOverlayComponent } from "./bob-toast-overlay.component";
import type { BobToastCardPayload, BobToastOptions } from "./bob-toast.types";

/**
 * Global Bob toast API — card overlay only, app uses bottom placement.
 * - `*Bottom` helpers: card above footer (default for all app call sites).
 * - `*Top` helpers: reserved for rare top placement (not used in app today).
 */
@Injectable({
  providedIn: "root",
})
export class BobToastService {
  private cardOverlayRef?: ComponentRef<BobToastOverlayComponent>;

  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);

  constructor(private translate: TranslateService) {}

  async show(message: string, options: BobToastOptions = {}): Promise<void> {
    const merged = this.mergeOptions(options);

    return this.ngZone.run(async () => {
      if (merged.replaceCurrent) {
        await this.dismissAll();
      }

      await this.deferPresentation(merged.deferMs ?? 80);
      return this.showCard(message, merged, false);
    });
  }

  async showKey(
    translationKey: string,
    options: BobToastOptions = {}
  ): Promise<void> {
    return this.show(this.translate.instant(translationKey), options);
  }

  success(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "success", options);
  }

  error(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "danger", options);
  }

  warning(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "warning", options);
  }

  info(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "medium", options);
  }

  showTop(
    message: string,
    color: BobToastCardPayload["color"] = "medium",
    options?: BobToastOptions
  ): Promise<void> {
    const merged = this.mergeOptions({ ...options, position: "top", color });
    return this.ngZone.run(async () => {
      if (merged.replaceCurrent) {
        await this.dismissAll();
      }
      await this.deferPresentation(merged.deferMs ?? 80);
      return this.showCard(message, merged, true);
    });
  }

  successTop(message: string, options?: BobToastOptions): Promise<void> {
    return this.showTop(message, "success", options);
  }

  errorTop(message: string, options?: BobToastOptions): Promise<void> {
    return this.showTop(message, "danger", options);
  }

  warningTop(message: string, options?: BobToastOptions): Promise<void> {
    return this.showTop(message, "warning", options);
  }

  infoTop(message: string, options?: BobToastOptions): Promise<void> {
    return this.showTop(message, "medium", options);
  }

  showBottom(
    message: string,
    color: BobToastCardPayload["color"] = "medium",
    options?: BobToastOptions
  ): Promise<void> {
    return this.show(message, { ...options, position: "bottom", color });
  }

  successBottom(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "success", options);
  }

  errorBottom(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "danger", options);
  }

  warningBottom(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "warning", options);
  }

  infoBottom(message: string, options?: BobToastOptions): Promise<void> {
    return this.showBottom(message, "medium", options);
  }

  profileIncomplete(message?: string, options?: BobToastOptions): Promise<void> {
    const body =
      message ??
      this.translate.instant("BOB_TOAST.PROFILE_INCOMPLETE_BODY");
    return this.show(body, {
      ...options,
      position: "bottom",
      color: "danger",
      title: this.translate.instant("BOB_TOAST.PROFILE_INCOMPLETE_TITLE"),
      badge: this.translate.instant("BOB_TOAST.BADGE_ERROR"),
      duration: options?.duration ?? 5000,
    });
  }

  locationError(message?: string, options?: BobToastOptions): Promise<void> {
    const text =
      message ??
      this.translate.instant("ERROR.LOCATION_NOT_AVAILABLE");
    return this.show(text, {
      ...options,
      position: "bottom",
      color: "danger",
      title: this.translate.instant("BOB_TOAST.LOCATION_ERROR_TITLE"),
      badge: this.translate.instant("BOB_TOAST.BADGE_ERROR"),
      icon: "location-outline",
      duration: options?.duration ?? 5000,
    });
  }

  demandUnavailable(message: string, options?: BobToastOptions): Promise<void> {
    return this.show(message, {
      ...options,
      position: "bottom",
      color: "warning",
      title: this.translate.instant("BOB_TOAST.DEMAND_UNAVAILABLE_TITLE"),
      badge: this.translate.instant("BOB_TOAST.BADGE_INFO"),
      icon: "warning-outline",
      duration: options?.duration ?? 4000,
    });
  }

  async dismiss(): Promise<boolean> {
    if (!this.cardOverlayRef) {
      return false;
    }
    this.dismissCard();
    return true;
  }

  async dismissAll(): Promise<void> {
    this.dismissCard();
  }

  private showCard(
    message: string,
    merged: BobToastOptions & typeof BOB_TOAST_DEFAULTS,
    allowTop: boolean
  ): Promise<void> {
    const color = merged.color ?? BOB_TOAST_DEFAULTS.color;
    const title = merged.title ?? message;
    const body = merged.title ? message : "";

    const clickable = !!(merged.onClick || merged.navigateTo);

    const payload: BobToastCardPayload = {
      message: body,
      title,
      badge: merged.badge ?? this.defaultBadgeForColor(color),
      color,
      icon: resolveBobToastIcon(color, merged.icon),
      position: resolveBobToastPosition(merged.position, allowTop),
      duration: merged.duration ?? BOB_TOAST_DEFAULTS.duration,
      showClose: merged.showClose ?? BOB_TOAST_DEFAULTS.showClose,
      clickable,
    };

    return new Promise((resolve) => {
      const ref = createComponent(BobToastOverlayComponent, {
        environmentInjector: this.envInjector,
      });
      ref.setInput("payload", payload);

      if (clickable) {
        ref.instance.cardClicked.subscribe(() => {
          this.ngZone.run(() => {
            void this.handleCardClick(merged, ref);
          });
        });
      }

      ref.instance.dismissed.subscribe(() => {
        if (this.cardOverlayRef === ref) {
          this.cardOverlayRef = undefined;
        }
        this.destroyCard(ref);
        resolve();
      });

      this.appRef.attachView(ref.hostView);
      ref.changeDetectorRef.detectChanges();
      this.appRef.tick();

      const parent = this.getOverlayParent();
      parent.appendChild(ref.location.nativeElement);
      this.cardOverlayRef = ref;
    });
  }

  private getOverlayParent(): HTMLElement {
    return document.querySelector("ion-app") ?? document.body;
  }

  private deferPresentation(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private dismissCard(): void {
    if (!this.cardOverlayRef) {
      return;
    }
    const ref = this.cardOverlayRef;
    // Emit `dismissed` so the showCard() Promise resolves AND its subscriber
    // performs the destroy. Without this, replacing a toast leaves the
    // previous Promise hanging forever (callers `await bobToast.show(...)`
    // would block indefinitely).
    try {
      ref.instance?.dismissed?.emit();
    } catch (_) {
      // Component instance already gone — fall back to manual cleanup.
      this.cardOverlayRef = undefined;
      this.destroyCard(ref);
    }
  }

  private destroyCard(ref: ComponentRef<BobToastOverlayComponent>): void {
    this.appRef.detachView(ref.hostView);
    ref.destroy();
  }

  private defaultBadgeForColor(color: BobToastCardPayload["color"]): string {
    switch (color) {
      case "danger":
        return this.translate.instant("BOB_TOAST.BADGE_ERROR");
      case "warning":
        return this.translate.instant("BOB_TOAST.BADGE_INFO");
      case "success":
        return this.translate.instant("BOB_TOAST.BADGE_SUCCESS");
      default:
        return this.translate.instant("BOB_TOAST.BADGE_INFO");
    }
  }

  private async handleCardClick(
    merged: BobToastOptions & typeof BOB_TOAST_DEFAULTS,
    ref: ComponentRef<BobToastOverlayComponent>
  ): Promise<void> {
    try {
      if (merged.onClick) {
        await merged.onClick();
      }
      if (merged.navigateTo) {
        const commands = this.resolveNavigateCommands(merged.navigateTo);
        await this.router.navigate(commands as any[]);
      }
    } finally {
      if (merged.dismissOnClick !== false) {
        ref.instance.close();
      } else {
        ref.instance.clickHandled = false;
      }
    }
  }

  private resolveNavigateCommands(
    navigateTo: string | readonly unknown[]
  ): unknown[] {
    if (Array.isArray(navigateTo)) {
      return [...navigateTo];
    }
    const route = navigateTo as string;
    const path = route.split("?")[0];
    const query = route.includes("?")
      ? Object.fromEntries(new URLSearchParams(route.split("?")[1]))
      : undefined;
    return query ? [path, { queryParams: query }] : [path];
  }

  private mergeOptions(
    options: BobToastOptions
  ): BobToastOptions & typeof BOB_TOAST_DEFAULTS {
    return {
      ...BOB_TOAST_DEFAULTS,
      ...options,
      color: options.color ?? BOB_TOAST_DEFAULTS.color,
      duration: options.duration ?? BOB_TOAST_DEFAULTS.duration,
      position: options.position ?? BOB_TOAST_DEFAULTS.position,
      replaceCurrent:
        options.replaceCurrent ?? BOB_TOAST_DEFAULTS.replaceCurrent,
      showClose: options.showClose ?? BOB_TOAST_DEFAULTS.showClose,
    };
  }
}
